import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import {
    type BookingBatch,
    type EnrichedAppointmentWithBookingStatus,
    selectCurrentForm,
    useBookingStore,
} from '@/stores/bookingStore.ts';

import { mergeBookedItemsByOrder } from '@/utils/collection-utils.ts';

interface BookingTaskContext {
    tasksEnabled: boolean;
    bookedAppointments: EnrichedAppointmentWithBookingStatus[];
    practitionerData: ReturnType<typeof useEnrichedPractitionerDataForSpecificInstance>['data'];
    currentPatient: GenericPatient | undefined;
    currentForm: ReturnType<typeof selectCurrentForm>;
    instanceId: string;
    currentBatch: BookingBatch | undefined;
}

export const useBookingTaskContext = (): BookingTaskContext => {
    const currentPatient = useBookingStore((state) => state.current_patient);
    const collectionBookings = useBookingStore((state) => state.collection_bookings);
    const batches = useBookingStore((state) => state.batches);
    const currentBatchId = useBookingStore((state) => state.current_batch_id);
    const isCollectionSubmitting = useBookingStore((state) => state.isCollectionSubmitting);
    const currentForm = useBookingStore(selectCurrentForm);

    const currentBatch = currentBatchId ? batches.get(currentBatchId) : undefined;
    const bookedAppointments = mergeBookedItemsByOrder(collectionBookings, currentBatch?.enriched_appointments).flatMap(
        (item) => (item.kind === 'collection' ? item.collection.appointments : [item.appointment])
    );

    const instanceId = currentPatient?.instance_id ?? '';
    const { data: practitionerData } = useEnrichedPractitionerDataForSpecificInstance(instanceId);

    const isDocCirrus = practitionerData?.pvs_provider === PVSProvider.DOC_CIRRUS;
    const tasksEnabled = isDocCirrus && (bookedAppointments.length > 0 || isCollectionSubmitting);

    return {
        tasksEnabled,
        bookedAppointments,
        practitionerData,
        currentPatient,
        currentForm,
        instanceId,
        currentBatch,
    };
};
